import { useState } from "react";
import { motion } from "framer-motion";
import { Emery } from "@/components/ui/Calla";
import { EMERY_MESSAGES } from "@/constants/callaMessages";
import { resendUserConfirmation } from "@/lib/email";

interface SuccessScreenProps {
  carrierName: string;
  vehicleInfo: string;
  userName: string;
  submissionId?: string;
  emailStatus?: "sent" | "failed";
}

const NEXT_STEPS = [
  "Emery calls your carrier (1-2 business days)",
  "You receive an email with findings",
  "Review and approve any changes",
] as const;

export function SuccessScreen({
  carrierName,
  vehicleInfo,
  userName,
  submissionId,
  emailStatus,
}: SuccessScreenProps) {
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "failed">(
    emailStatus === "failed" ? "idle" : "idle"
  );

  const handleResend = async () => {
    if (!submissionId) return;
    setResendState("sending");
    try {
      await resendUserConfirmation(submissionId);
      setResendState("sent");
    } catch (err) {
      console.error("[Email] resend failed", err);
      setResendState("failed");
    }
  };

  const showBanner = emailStatus === "failed" && resendState !== "sent";

  return (
    <div className="max-w-lg mx-auto">
      <Emery message={EMERY_MESSAGES.success} />

      {showBanner && (
        <div
          className="mb-6 rounded-lg p-4 text-sm"
          style={{ border: "1px solid #fde68a", background: "#FFFBEB", color: "#92400e" }}
        >
          <p className="mb-2">
            We saved your submission but couldn't email you a copy right now. Your
            information is safe.
          </p>
          {submissionId && (
            <button
              type="button"
              onClick={handleResend}
              disabled={resendState === "sending"}
              className="underline font-medium disabled:opacity-60"
            >
              {resendState === "sending"
                ? "Sending..."
                : resendState === "failed"
                ? "Try again"
                : "Resend confirmation email"}
            </button>
          )}
          {resendState === "failed" && (
            <p className="mt-2 text-xs">
              Still not working. We'll keep trying and reach out if anything is needed.
            </p>
          )}
        </div>
      )}

      {resendState === "sent" && (
        <div
          className="mb-6 rounded-lg p-4 text-sm"
          style={{ border: "1px solid #bbf7d0", background: "#ECFDF5", color: "#166534" }}
        >
          Confirmation email sent. Check your inbox in a minute.
        </div>
      )}

      {/* Animated checkmark */}
      <motion.svg
        viewBox="0 0 52 52"
        className="w-16 h-16 mx-auto mb-6"
        aria-label="Success"
      >
        <motion.circle
          cx="26"
          cy="26"
          r="24"
          fill="none"
          stroke="#30A46C"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
        <motion.path
          d="M14 27l7 7 16-16"
          fill="none"
          stroke="#30A46C"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.3, delay: 0.4, ease: "easeOut" }}
        />
      </motion.svg>

      {/* Submission summary */}
      <motion.div
        className="rounded-xl border p-5 mb-8"
        style={{
          borderColor: "var(--border-default)",
          backgroundColor: "var(--bg-sensitive-field)",
        }}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.6 }}
      >
        <h3
          className="text-sm font-semibold mb-3"
          style={{ color: "var(--text-secondary)" }}
        >
          Submission Summary
        </h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt style={{ color: "var(--text-muted)" }}>Carrier</dt>
            <dd className="font-medium" style={{ color: "var(--text-primary)" }}>
              {carrierName}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: "var(--text-muted)" }}>Vehicle</dt>
            <dd className="font-medium" style={{ color: "var(--text-primary)" }}>
              {vehicleInfo}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: "var(--text-muted)" }}>Name</dt>
            <dd className="font-medium" style={{ color: "var(--text-primary)" }}>
              {userName}
            </dd>
          </div>
        </dl>
      </motion.div>

      {/* What happens next timeline */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.8 }}
      >
        <h3
          className="text-sm font-semibold mb-4"
          style={{ color: "var(--text-secondary)" }}
        >
          What happens next
        </h3>
        <ol className="relative space-y-6 pl-1">
          {NEXT_STEPS.map((step, i) => (
            <li key={i} className="flex items-start gap-4">
              {/* Numbered circle with connecting line */}
              <div className="relative flex flex-col items-center">
                <div
                  className="flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold text-white flex-shrink-0"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  {i + 1}
                </div>
                {i < NEXT_STEPS.length - 1 && (
                  <div
                    className="w-px h-6 mt-1"
                    style={{ backgroundColor: "var(--border-default)" }}
                  />
                )}
              </div>
              <p
                className="text-sm pt-1.5"
                style={{ color: "var(--text-primary)" }}
              >
                {step}
              </p>
            </li>
          ))}
        </ol>
      </motion.div>
    </div>
  );
}
